import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import supabase from '@/supabaseClient'
import { useLibraryGames } from './useLibraryGames'
import FilterControls from './FilterControls'
import GameList from './GameList'

export interface LibraryHandle {
  refreshGames: () => Promise<void>
}

const normalizeSearchParams = (params: URLSearchParams) => {
  const orderedKeys = [
    'status',
    'platform',
    'genre',
    'mood',
    'year',
    'sort',
    'q',
    'letter',
    'page',
  ]
  const normalized = new URLSearchParams()
  orderedKeys.forEach((key) => {
    const value = params.get(key)
    if (value) normalized.set(key, value)
  })
  return normalized.toString()
}

interface LibraryProps {
  onAddGame?: () => void
}

const Library: React.ForwardRefRenderFunction<LibraryHandle, LibraryProps> = (
  { onAddGame },
  ref
) => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const hasInitializedFilters = useRef(false)
  const lastSyncedQuery = useRef<string | null>(null)
  const [userId, setUserId] = useState<string>('')

  useEffect(() => {
    const getCurrentUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (user) {
        setUserId(user.id)
      }
    }
    getCurrentUser()
  }, [])


  const [selectedLetter, setSelectedLetter] = useState<string>('')
  const {
    games,
    fetchGames,
    page,
    setPage,
    pageSize,
    totalCount,
    filters,
    setters,
    options,
    error,
    loading,
  } = useLibraryGames(userId, selectedLetter)

  // Apply initial filters from navigation state only once on mount
  useEffect(() => {
    if (location.state) {
      const {
        filterStatus,
        filterYear,
        filterPlatform,
        filterGenre,
        filterMood,
        sortOrder,
        searchQuery,
      } = location.state as Record<string, any>
      if (filterStatus)
        setters.setFilterStatus(
          Array.isArray(filterStatus) ? filterStatus : [filterStatus]
        )
      if (typeof filterYear !== 'undefined') setters.setFilterYear(filterYear)
      if (filterPlatform) setters.setFilterPlatform(filterPlatform)
      if (filterGenre) setters.setFilterGenre(filterGenre)
      if (filterMood) setters.setFilterMood(filterMood)
      if (sortOrder) setters.setSortOrder(sortOrder)
      if (searchQuery) setters.setSearchQuery(searchQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const hasQueryParams = Array.from(searchParams.keys()).length > 0
    if (!hasQueryParams && location.state && !hasInitializedFilters.current) {
      hasInitializedFilters.current = true
      return
    }

    const normalizedQuery = normalizeSearchParams(searchParams)
    if (
      hasInitializedFilters.current &&
      lastSyncedQuery.current === normalizedQuery
    ) {
      return
    }

    const statusParam = searchParams.get('status') || ''
    const statuses = statusParam
      .split(',')
      .map((status) => status.trim())
      .filter(Boolean)
    const filterPlatform = searchParams.get('platform') || ''
    const filterGenre = searchParams.get('genre') || ''
    const filterMood = searchParams.get('mood') || ''
    const filterYearParam = searchParams.get('year')
    const filterYear = filterYearParam ? Number(filterYearParam) : null
    const sortOrder = searchParams.get('sort') || 'alphabetical-asc'
    const searchQuery = searchParams.get('q') || ''
    const letterParam = searchParams.get('letter') || ''
    const pageParam = searchParams.get('page')
    const nextPage = pageParam ? Number(pageParam) : 1

    setters.setFilterStatus(statuses)
    setters.setFilterPlatform(filterPlatform)
    setters.setFilterGenre(filterGenre)
    setters.setFilterMood(filterMood)
    setters.setFilterYear(Number.isFinite(filterYear) ? filterYear : null)
    setters.setSortOrder(sortOrder)
    setters.setSearchQuery(searchQuery)
    setSelectedLetter(letterParam)
    setPage(Number.isFinite(nextPage) && nextPage > 0 ? nextPage : 1)
    hasInitializedFilters.current = true
    lastSyncedQuery.current = normalizedQuery
  }, [searchParams, setters, setPage, location.state])

  useEffect(() => {
    if (!hasInitializedFilters.current) return
    const nextParams = new URLSearchParams()

    if (filters.filterStatus.length > 0) {
      nextParams.set('status', filters.filterStatus.join(','))
    }
    if (filters.filterPlatform) {
      nextParams.set('platform', filters.filterPlatform)
    }
    if (filters.filterGenre) nextParams.set('genre', filters.filterGenre)
    if (filters.filterMood) nextParams.set('mood', filters.filterMood)
    if (filters.filterYear !== null) {
      nextParams.set('year', String(filters.filterYear))
    }
    if (filters.sortOrder && filters.sortOrder !== 'alphabetical-asc') {
      nextParams.set('sort', filters.sortOrder)
    }
    if (filters.searchQuery) nextParams.set('q', filters.searchQuery)
    if (selectedLetter) nextParams.set('letter', selectedLetter)
    if (page > 1) nextParams.set('page', String(page))

    const nextQuery = normalizeSearchParams(nextParams)
    const currentQuery = normalizeSearchParams(searchParams)
    if (nextQuery !== currentQuery) {
      lastSyncedQuery.current = nextQuery
      setSearchParams(nextParams, { replace: true })
    }
  }, [
    filters.filterStatus,
    filters.filterPlatform,
    filters.filterGenre,
    filters.filterMood,
    filters.filterYear,
    filters.sortOrder,
    filters.searchQuery,
    page,
    selectedLetter,
    setSearchParams,
    searchParams,
  ])
  useImperativeHandle(ref, () => ({
    refreshGames: fetchGames,
  }))

  return (
    <div className="container mx-auto p-4 overflow-x-hidden">
      <FilterControls filters={filters} setters={setters} options={options} />
      {/* A-Z Filter Bar */}
      <div className="flex flex-wrap gap-1 justify-center my-4">
        {[...Array(26)].map((_, i) => {
          const letter = String.fromCharCode(65 + i)
          return (
            <button
              key={letter}
              className={`px-2 py-1 rounded text-sm font-semibold border border-base-300 transition-colors duration-100 ${
                selectedLetter === letter
                  ? 'bg-primary text-white'
                  : 'bg-base-200 hover:bg-primary/20'
              }`}
              onClick={() => {
                setSelectedLetter(selectedLetter === letter ? '' : letter)
                setPage(1)
              }}
              type="button"
            >
              {letter}
            </button>
          )
        })}
        <button
          className={`px-2 py-1 rounded text-sm font-semibold border border-base-300 transition-colors duration-100 ${
            selectedLetter === '#'
              ? 'bg-primary text-white'
              : 'bg-base-200 hover:bg-primary/20'
          }`}
          onClick={() => {
            setSelectedLetter(selectedLetter === '#' ? '' : '#')
            setPage(1)
          }}
          type="button"
        >
          #
        </button>
        <button
          className={`px-2 py-1 rounded text-sm font-semibold border border-base-300 transition-colors duration-100 ${
            selectedLetter === ''
              ? 'bg-primary text-white'
              : 'bg-base-200 hover:bg-primary/20'
          }`}
          onClick={() => {
            setSelectedLetter('')
            setPage(1)
          }}
          type="button"
        >
          All
        </button>
      </div>
      {error && (
        <div className="alert alert-error my-4" role="alert">
          {error}
        </div>
      )}
      {loading ? (
        <div className="text-center text-base-content/50 my-6 text-sm">Loading your games…</div>
      ) : !games.length && !error && !filters.filterStatus.length && !filters.filterPlatform && !filters.filterGenre && !filters.filterMood && !filters.filterYear && !filters.searchQuery && !selectedLetter ? (
        <div className="text-center py-16 max-w-sm mx-auto">
          <p className="text-5xl mb-6">🎮</p>
          <h2 className="text-2xl font-bold font-serif mb-3" style={{ color: '#3d2a1e' }}>
            Your library is empty — for now.
          </h2>
          <p className="text-base-content/60 mb-8">
            Add a game you've been meaning to play. Even just one. ✦
          </p>
          {onAddGame && (
            <button
              className="btn btn-primary rounded-full px-8"
              onClick={onAddGame}
            >
              + Add a game
            </button>
          )}
        </div>
      ) : !games.length && !error ? (
        <div className="text-center py-16 max-w-sm mx-auto">
          <p className="text-base-content/50 text-sm">
            Nothing matches those filters. Try loosening them — you've got more in there than you think.
          </p>
        </div>
      ) : (
        <>
          <GameList games={games} userId={userId} onRefresh={fetchGames} />
          <div className="flex flex-wrap justify-center items-center gap-2 mt-6 w-full">
            <button
              className="btn btn-outline"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span className="truncate min-w-0">
              Page {page} of {Math.max(1, Math.ceil(totalCount / pageSize))}
            </span>
            <button
              className="btn btn-outline"
              disabled={page * pageSize >= totalCount}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default forwardRef(Library)
